import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, LABEL, RESULT_CARD } from "@/components/tool-kit/styles";
import {
  EQUATION_ERROR_STRINGS,
  type EquationErrorStrings,
  EquationView,
  equationErrorMessage,
} from "@/components/tools/chem-reactions/equation-view";
import {
  type BalancedEquation,
  orientEquation,
} from "@/lib/tool-kit/chem-reactions/balance";

/**
 * The equation field, its example buttons and the balanced result every stoichiometry
 * calculator starts from. Widgets spread `BALANCED_EQUATION_STRINGS` into their own strings.
 */
export const BALANCED_EQUATION_STRINGS = {
  equationLabel: "Chemical equation",
  equationHint:
    "Balanced or not — the coefficients are worked out for you. Write ions with a sign (Fe3+, SO4^2-) and add (g) after a gas if you want its volume.",
  equationPlaceholder: "N2 + H2 -> NH3",
  tryLabel: "Try an example",
  balancedHeading: "Balanced equation",
  ignoredNote:
    "The coefficients you typed were ignored: the calculator balances the equation itself.",
  unbalancedBody:
    "This equation has no single balance with every species on the side you wrote it, so the mole ratios are not defined. Fix it with the Chemical Equation Balancer, which explains what is wrong, then come back.",
  errors: EQUATION_ERROR_STRINGS,
} as const;

type BalancedStrings = {
  [K in keyof typeof BALANCED_EQUATION_STRINGS]: K extends "errors"
    ? EquationErrorStrings
    : string;
};

type EquationFieldStrings = Pick<
  BalancedStrings,
  "equationLabel" | "equationHint" | "equationPlaceholder" | "tryLabel"
>;

export function EquationField({
  id,
  strings,
  value,
  onChange,
  examples,
}: {
  id: string;
  strings: EquationFieldStrings;
  value: string;
  onChange: (value: string) => void;
  examples: readonly string[];
}) {
  return (
    <div className="space-y-3">
      <TextField
        hint={strings.equationHint}
        id={id}
        label={strings.equationLabel}
        mono
        onChange={onChange}
        placeholder={strings.equationPlaceholder}
        value={value}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.tryLabel}</span>
        {examples.map((example) => (
          <button
            className={BUTTON_SMALL}
            key={example}
            onClick={() => onChange(example)}
            type="button"
          >
            {example}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The balanced equation as a result card, or the reason there is not one. */
export function BalancedEquationCard({
  solved,
  strings,
}: {
  solved: BalancedEquation;
  strings: BalancedStrings;
}) {
  if (solved.kind === "parse") {
    if (solved.error.code === "empty") {
      return <Notice>{strings.errors.empty}</Notice>;
    }
    return (
      <ErrorNotice>
        {equationErrorMessage(strings.errors, solved.error)}
      </ErrorNotice>
    );
  }
  if (solved.kind === "unbalanced") {
    return <ErrorNotice>{strings.unbalancedBody}</ErrorNotice>;
  }
  const oriented = orientEquation(solved.equation, solved.coefficients);
  return (
    <>
      <div className={RESULT_CARD}>
        <p className="font-medium text-muted-foreground text-sm">
          {strings.balancedHeading}
        </p>
        <p className="mt-2 font-semibold text-foreground text-xl leading-relaxed">
          <EquationView oriented={oriented} />
        </p>
      </div>
      {solved.equation.ignoredCoefficients ? (
        <Notice>{strings.ignoredNote}</Notice>
      ) : null}
    </>
  );
}
